import { useCookies } from 'react-cookie';
import { customFetch } from '../customFetch';
import { useImageGenerationSettingsContext } from '../../context/ImageGenerationSettingsProvider';
import {
  getRandomSeed,
  getHasAcceptedTerms,
  getCreditsRequired,
} from '../../components/helpers';
import { useGeneralContext } from '../../context/GeneralContextProvider';
import { EngineType, FakeImage } from '../../types';
import { scrollToTop } from '../misc/helpers';
import { v4 as uuidv4 } from 'uuid';
import { toast } from 'react-toastify';
import { addImageToLocalStorage } from '../../localStorage/imageStorage';
import { waitSeconds } from '../../components/helpers';
import { useLoggedInUserContext } from '../../context/LoggedInUserContextProvider';
import { useModalsContext } from '../../context/ModalsContextProvider';
import { getIsOnDesktop } from '../../styleHelpers';
import { PLACEHOLDER_IMAGE_PATH } from '../../components/ImageStuff/GeneratedImage';
import useHandleDeductCredits from './useHandleDeductCredits';
import { useChatContext } from '../../context/ChatContextProvider';
import useChatHelpers from '../chat/useChatHelpers';
import {
  blockAnimalWords,
  checkPromptForBlocking,
  checkPromptForCelebs,
} from '../../utils/clientSideBlocking';
import { getBadWord } from '../misc/getBadWords';
import { XLWaitTime } from '../../generationConstants';

const useHandleSendXLImageRequest = () => {
  const [{ token }] = useCookies(['token']);
  const [, setFetchToken] = useCookies(['fetchToken']);
  const { loggedInUser } = useLoggedInUserContext();
  const {
    isSeedFrozen,
    setActiveImage,
    setImages,
    setEstimateGenerationReadyTimestamp,
    setGenerationStartedTimestamp,
    setLoading,
    loading,
    isFirstTimeUser,
    firstTimeUserToken,
    setFirstTimeUserToken,
    setShowTopRightMenu,
    setHasGeneratedAnImage,
  } = useGeneralContext();
  const {
    userPrompt,
    negativePrompt,
    seedState: seed,
    setSeed,
    size,
    cfg,
    amountToGenerate,
    uploadedImageUrl,
    speedMode,
    setSpeedMode,
    engine,
    selectedStyle,
    denoisingStrength,
    selectedTags,
  } = useImageGenerationSettingsContext();

  const handleDeductCredits = useHandleDeductCredits();

  const {
    setShowHintToRegisterModal,
    hasClosedHintToRegisterModal,
    setIsAuthModalOpen,
    setAuthModalVariant,
    setShowFirstTimeModal,
  } = useModalsContext();

  const { setShowCreateImageModal } = useChatContext();
  const { addImageToActiveChat, setCurrentImage } = useChatHelpers();

  const getSeed = () => {
    if (isSeedFrozen) {
      if (seed > 4294967294) return getRandomSeed();
      if (seed === 0) return getRandomSeed();
      return seed as number;
    }

    const randomSeed = getRandomSeed();
    setSeed(randomSeed);
    return randomSeed;
  };

  const handleSendXLImageRequest = async (
    inChatGenerator?: boolean,
    options?: {
      fullPrompt?: string;
      negativePrompt?: string;
      engine?: EngineType;
      isTallPortrait?: boolean;
    },
  ): Promise<string> => {
    const hasAcceptedTerms = getHasAcceptedTerms();
    if (!hasAcceptedTerms) {
      setShowFirstTimeModal(true);
    }

    const animalBlockResult = blockAnimalWords(userPrompt);
    if (animalBlockResult.shouldBlock) {
      alert(
        `Please do not use "${animalBlockResult.matchedWords?.join(
          ', ',
        )}" in your prompt`,
      );
      setLoading(false);
      return 'Please do not use animal words in your prompt';
    }

    const isBannedRegex = checkPromptForBlocking(userPrompt);
    if (isBannedRegex) {
      alert(
        `Forbidden prompt. If you think this is a mistake, please contact hello@TheUncensored.AI with the prompt`,
      );
      setLoading(false);
      return 'Forbidden prompt';
    }

    const isCelebPrompt = checkPromptForCelebs(userPrompt);
    if (isCelebPrompt) {
      alert(
        'Please do not use celeb names in your prompt. If you think this is a mistake, please contact hello@TheUncensored.AI',
      );
      setLoading(false);
      return 'Forbidden prompt';
    }

    const badWord = getBadWord(userPrompt);
    if (badWord) {
      alert(`Please do not use "${badWord}" in your prompt`);
      setLoading(false);
      return 'Forbidden prompt';
    }

    // We use effectiveEngine instead of state management to avoid race conditions
    // between different engine modes (adTurbo vs turbo) and ensure consistent engine
    // selection throughout the generation process
    let effectiveEngine = engine;

    if (loggedInUser?.username !== 'malossi' && loading) {
      toast.error('Please wait for the current generation to complete');
      return 'Please wait for the current generation to complete';
    }

    if (
      !loggedInUser &&
      !hasClosedHintToRegisterModal &&
      speedMode === 'turbo' &&
      !isFirstTimeUser
    ) {
      setShowHintToRegisterModal(true);
      return 'Please sign up to continue generating';
    }

    const isDesktop = getIsOnDesktop();
    if (speedMode === 'adTurbo' && isDesktop) {
      alert(
        "adTurbo temporarily offline for desktop, since ad blockers are able to block the ad, thus we lose money and can't afford it",
      );
      setSpeedMode('turbo');
      return 'No adTurbo for desktop';
    }

    // @ts-ignore
    if (speedMode !== 'turbo') {
      toast.error('XL is only available in Turbo mode');
      setSpeedMode('turbo');
      return 'Please wait use turbo mode';
    }

    setLoading(true);
    const now = Date.now();
    setGenerationStartedTimestamp(now);

    const getExpectedWait = () => {
      return XLWaitTime * 1000;
    };

    setEstimateGenerationReadyTimestamp(now + getExpectedWait());

    /*
    // Ads are not in use here
    if (speedMode === 'adTurbo') {
      if (!hasSeenAdInfoText) {
        toast.info(
          'The ads, unfortunately, pay very little, and the servers are very expensive. Due to this we have to show multiple ads to avoid going bankrupt. Sorry!',
          { autoClose: 5000 },
        );
        setHasSeenAdInfoText(true);
        await waitSeconds(5);
      }

      const resultOfAdStuff = handlePopUnderAd(speedMode);

      if (!resultOfAdStuff) {
        setLoading(false);
        return 'Blocked by ad blocker';
      }

      await waitSeconds(0.5);

      const resultOfAdStuff2 = handlePopUnderAd(speedMode);
      if (!resultOfAdStuff2) {
        setLoading(false);
        return 'Blocked by ad blocker';
      }

      await waitSeconds(0.5);

      const resultOfAdStuff3 = handlePopUnderAd(speedMode);
      if (!resultOfAdStuff3) {
        setLoading(false);
        return 'Blocked by ad blocker';
      }

      await waitSeconds(0.5);

      const resultOfAdStuff4 = handlePopUnderAd(speedMode);
      if (!resultOfAdStuff4) {
        setLoading(false);
        return 'Blocked by ad blocker';
      }
    }
    */

    const creditsRequired = getCreditsRequired(
      speedMode,
      amountToGenerate,
      effectiveEngine,
    );

    const handleDeductCreditsResult = handleDeductCredits(creditsRequired);

    if (handleDeductCreditsResult !== 'ok') {
      if (!loggedInUser) {
        toast.error(
          'You need to sign up for free credits to continue generating',
        );
        await waitSeconds(1);
        setIsAuthModalOpen(true);
        setAuthModalVariant('register');
        setLoading(false);
        return 'Please sign up to continue generating';
      }
      toast.error("You don't have enough credits to use this engine");
      await waitSeconds(2);
      setShowTopRightMenu(true);
      setLoading(false);
      return 'You dont have enough credits to use this engine';
    }

    if (amountToGenerate > 1) {
      toast.error(
        'Currently only one image can be generated with Super at a time',
      );
      setLoading(false);
      return 'Currently only one image can be generated with this engine at a time';
    }

    const functionName = 'generateXLImage';

    const trackId = uuidv4();

    const promptToUse = options?.fullPrompt ?? userPrompt;
    const negativePromptToUse = options?.negativePrompt ?? negativePrompt;
    const engineToUse = options?.engine ?? effectiveEngine; // Using effectiveEngine ensures consistent engine throughout request, stupid bugs and race conditions etc.
    const sizeToUse = options?.isTallPortrait
      ? { width: 512, height: 768 }
      : size;

    const imageRequestObject = {
      fullPrompt: promptToUse,
      negativePrompt: negativePromptToUse,
      seed: getSeed(),
      size: sizeToUse,
      cfg,
      amountToGenerate,
      uploadedImageUrl,
      trackId,
      engine: engineToUse,
      speedMode,
      style: selectedStyle,
      ...(firstTimeUserToken && { firstTimeUserToken }),
      denoisingStrength,
      isCreatingVariations: !!uploadedImageUrl,
    };

    const response = await customFetch(functionName, {
      imageRequestObject,
      token,
    });

    const { url, eta, fetchToken } = response;

    if (eta === 420) {
      await waitSeconds(15);
      alert(
        "You have been banned by the automatic system. If you're sure you didn't break the rules, please send your username to hello@TheUncensored.AI. Sorry for the inconvenience!",
      );
      return "You have been banned by the automatic system. If you're sure you didn't break the rules, please send your username to hello@TheUncensored.AI. Sorry for the inconvenience!";
    }
    if (!url) {
      toast.error('Failed to generate image');
      setLoading(false);
      return 'Failed to generate image';
    }

    // Update estimation with actual eta from server
    if (eta) {
      setEstimateGenerationReadyTimestamp(now + getExpectedWait() + eta * 1000);

      if (eta > 140) {
        toast.warning(
          "Image server seems to be experiencing some issues. It's possible the image won't load even after the timer. Contact Hello@TheUncensored.AI for credit refund",
          { autoClose: 10000 },
        );
      }
    }

    if (fetchToken) {
      setHasGeneratedAnImage(true);
      const maxAge = 7776000;
      setFetchToken('fetchToken', fetchToken, {
        path: '/',
        secure: true,
        sameSite: 'strict',
        maxAge,
      });
    }

    setFirstTimeUserToken('');

    const imageToAdd: FakeImage = {
      imageUrl: PLACEHOLDER_IMAGE_PATH,
      futureUrl: url,
      generating: true,
      trackId,
      guidanceScale: cfg,
      engine: effectiveEngine,
      negativePrompt: negativePromptToUse,
      prompt: promptToUse,
      seed: imageRequestObject.seed,
      height: Number(size.height),
      width: Number(size.width),
      uploadedImageUrl,
      isCreatingVariations: !!uploadedImageUrl,
      style: selectedStyle,
      selectedTags,
    };

    setImages((prevImages) => [...prevImages, imageToAdd]);
    setActiveImage(imageToAdd);
    addImageToLocalStorage(imageToAdd);

    if (inChatGenerator) {
      setLoading(false);
      if (imageToAdd.futureUrl) {
        imageToAdd.imageUrl = imageToAdd.futureUrl;
        imageToAdd.generating = false;
        addImageToActiveChat(imageToAdd);
        setShowCreateImageModal(false);
        toast.info('Creating the image. This might take up to 30 seconds');
        await waitSeconds(15);
        setCurrentImage(imageToAdd.futureUrl);
      } else {
        toast.error('Failed to generate image');
      }
    }

    scrollToTop();

    return url;
  };

  return handleSendXLImageRequest;
};

export default useHandleSendXLImageRequest;
